import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { getSuperlativesAction, selectAllSuperlatives } from "../superlativeSlice";
import { selectLeaderboardPlayers } from "../../leaderboard/leaderboardSlice";
import { selectGameId, selectGameObject } from "../../gamestate/gameStateSlice";
import GameboardLeaderboard from "../../gameboard/components/GameBoardLeaderboard";
import { BrandLockup, BrandMark } from "components";

const Root = styled(Box)({
  width: "100%",
  height: "100vh",
  background: "linear-gradient(135deg, #faf2d2 0%, #eeeeee 50%, #e1f5f3 100%)",
  position: "fixed",
  top: 0,
  left: 0,
  overflow: "hidden",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
});

const LeaderboardOverlay = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: theme.spacing(3),
  left: theme.spacing(3),
  width: 360,
  zIndex: 1,
}));

const Content = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  padding: "24px 408px 28px",
  width: "100%",
  maxWidth: 2200,
  height: "100%",
  gap: 16,
});

const TopBar = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flexShrink: 0,
});


const GameTitleLine = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "1.3rem",
  color: "#222",
  textAlign: "center",
  fontWeight: 700,
  marginTop: 10,
});

const DateLine = styled(Typography)({
  fontSize: "0.85rem",
  letterSpacing: "0.3em",
  color: "#888",
  textAlign: "center",
  marginTop: 6,
  textTransform: "uppercase",
});

const Hero = styled(Box)({
  textAlign: "center",
  paddingBottom: 14,
  borderBottom: "2px dashed rgba(0,0,0,0.12)",
  flexShrink: 0,
});

const HeroLabel = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.9rem",
  letterSpacing: "0.32em",
  color: "#2ec4b6",
  fontWeight: 700,
  textTransform: "uppercase",
});

const HeroRow = styled(Box)({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "center",
  gap: 24,
  marginTop: 6,
  flexWrap: "wrap",
});

const HeroName = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "3.4rem",
  color: "#222",
  lineHeight: 1,
});

const HeroScore = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "2.4rem",
  color: "#1a7a72",
  lineHeight: 1,
});

const SectionLabel = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.85rem",
  letterSpacing: "0.32em",
  color: "#888",
  textAlign: "center",
  textTransform: "uppercase",
  flexShrink: 0,
});

const HighlightsGrid = styled(Box, { shouldForwardProp: (p) => p !== "cols" })(({ cols }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${cols}, 1fr)`,
  gridAutoRows: "minmax(min-content, 1fr)",
  gap: 12,
  flex: 1,
  minHeight: 0,
}));

const HighlightCard = styled(Box)({
  background: "rgba(255,255,255,0.7)",
  borderLeft: "4px solid #2ec4b6",
  borderRadius: 8,
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 4,
});

const HighlightHeader = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "0.85rem",
  letterSpacing: "0.18em",
  color: "#0f4a44",
  textTransform: "uppercase",
  fontWeight: 1000,
  lineHeight: 1.1,
});

const HighlightSub = styled(Typography)({
  fontSize: "0.8rem",
  color: "#666",
  lineHeight: 1.2,
});

const HighlightWinner = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "1.4rem",
  color: "#1a7a72",
  lineHeight: 1.1,
  fontWeight: 700,
  wordBreak: "break-word",
});

const Footer = styled(Box)({
  paddingTop: 10,
  borderTop: "2px dashed rgba(0,0,0,0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 14,
  flexShrink: 0,
});

const FooterText = styled(Typography)({
  fontSize: "1.1rem",
  color: "#444",
  fontWeight: 600,
});

const FooterURL = styled(Typography)({
  fontFamily: "Helvetica Inserat",
  fontSize: "1.8rem",
  color: "#1a7a72",
  fontWeight: 900,
  letterSpacing: "0.04em",
});

const formatDate = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date();
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return new Date().toLocaleDateString();
  }
};

// Pick columns to avoid awkward partially-filled trailing rows.
// 1–2 → 1 col; 3–4 → 2 cols; 5–9 → 3 cols; 10+ → 4 cols.
const pickColumnCount = (count) => {
  if (count <= 2) return 1;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  return 4;
};

const FinalePoster = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const superlatives = useSelector(selectAllSuperlatives);
  const players = useSelector(selectLeaderboardPlayers);
  const game = useSelector(selectGameObject);

  useEffect(() => {
    if (gameId) dispatch(getSuperlativesAction(gameId));
  }, [gameId, dispatch]);

  const winner = players?.[0];
  const dateStr = useMemo(() => formatDate(game?.createdAt), [game]);
  const items = superlatives || [];
  const cols = pickColumnCount(items.length);

  return (
    <Root>
      <LeaderboardOverlay>
        <GameboardLeaderboard />
      </LeaderboardOverlay>
      <Content>
        <TopBar>
          <BrandLockup size="xl" variant="light" />
          {game?.title && <GameTitleLine>{game.title}</GameTitleLine>}
          <DateLine>{dateStr}</DateLine>
        </TopBar>

        <Hero>
          <HeroLabel>🏆 Winner</HeroLabel>
          <HeroRow>
            <HeroName>{winner?.name || "—"}</HeroName>
            <HeroScore>${(winner?.score ?? 0).toLocaleString("en-US")}</HeroScore>
          </HeroRow>
        </Hero>

        <SectionLabel>The Highlights</SectionLabel>
        <HighlightsGrid cols={cols}>
          {items.map((s, i) => {
            // Center the trailing partial row by offsetting its grid column.
            const remainder = items.length % cols;
            const fullRowsCount = items.length - remainder;
            const inLastRow = remainder > 0 && i >= fullRowsCount;
            const startCol = Math.floor((cols - remainder) / 2) + 1;
            const sx = inLastRow
              ? { gridColumn: `${startCol + (i - fullRowsCount)} / span 1` }
              : undefined;
            return (
              <HighlightCard key={s.id} sx={sx}>
                <HighlightHeader>{s.header}</HighlightHeader>
                {s.subhead && <HighlightSub>{s.subhead}</HighlightSub>}
                <HighlightWinner>{s.winner}</HighlightWinner>
              </HighlightCard>
            );
          })}
        </HighlightsGrid>

        <Footer>
          <BrandMark size="sm" />
          <FooterText>Play your own game at</FooterText>
          <FooterURL>GROUPARDY.COM</FooterURL>
        </Footer>
      </Content>
    </Root>
  );
};

export default FinalePoster;
